import {Link} from 'react-router-dom';
import styled from 'styled-components';
import type {Ticket} from '../types/ticket';
import {formatDateShort} from '../utils/formatDate';

const PRIORITY_COLORS: Record<string, string> = {
	p0: '#ef4444',
	p1: '#fb923c',
	p2: '#fbbf24',
	p3: '#cbd5e1',
};

function getInitials(name: string): string {
	const parts = name.trim().split(/\s+/);
	if (parts.length >= 2) {
		return `${parts[0]![0]}${parts[parts.length - 1]![0]}`.toUpperCase();
	}
	return name.slice(0, 2).toUpperCase();
}

interface Props {
	ticket: Ticket;
}

const Card = styled(Link)`
	display: block;
	border-radius: 8px;
	border: 1px solid #e2e8f0;
	background: #ffffff;
	padding: 16px;
	box-shadow: 0 1px 2px rgb(0 0 0 / 0.05);
	transition: box-shadow 0.15s;
	text-decoration: none;
	color: inherit;
	&:hover {
		box-shadow: 0 4px 6px -1px rgb(0 0 0 / 0.1);
	}
`;
const TopRow = styled.div`
	display: flex;
	align-items: center;
	justify-content: space-between;
`;
const SlugGroup = styled.div`
	display: flex;
	align-items: center;
	gap: 8px;
`;
const Slug = styled.span`
	font-family: ui-monospace, SFMono-Regular, Menlo, monospace;
	font-size: 12px;
	line-height: 16px;
	font-weight: 500;
	color: #94a3b8;
`;
const Dot = styled.span<{$color: string}>`
	height: 8px;
	width: 8px;
	border-radius: 9999px;
	background: ${p => p.$color};
`;
const Meta = styled.span`
	${p => p.theme.sys.font?.detail?.['1']}
	color: #94a3b8;
`;
const Subject = styled.p`
	margin-top: 8px;
	display: -webkit-box;
	-webkit-line-clamp: 2;
	-webkit-box-orient: vertical;
	overflow: hidden;
	${p => p.theme.sys.font?.subheading?.['2']}
	color: #1e293b;
`;
const BottomRow = styled.div`
	margin-top: 12px;
	display: flex;
	align-items: center;
	justify-content: space-between;
`;
const PersonGroup = styled.div`
	display: flex;
	align-items: center;
	gap: 8px;
`;
const Avatar = styled.span<{$bg: string; $fg: string; $weight?: number}>`
	display: inline-flex;
	height: 24px;
	width: 24px;
	align-items: center;
	justify-content: center;
	border-radius: 9999px;
	background: ${p => p.$bg};
	color: ${p => p.$fg};
	${p => p.theme.sys.font?.detail?.['1']}
	font-weight: ${p => p.$weight ?? 500};
`;
const RequesterName = styled.span`
	max-width: 120px;
	overflow: hidden;
	text-overflow: ellipsis;
	white-space: nowrap;
	${p => p.theme.sys.font?.detail?.['1']}
	color: #64748b;
`;

export function TicketListCard({ticket}: Props) {
	const createdStr = formatDateShort(ticket.created_at);

	return (
		<Card to={`/tickets/${ticket.slug}`} aria-label={`Ticket ${ticket.slug}: ${ticket.subject}`}>
			<TopRow>
				<SlugGroup>
					<Slug>{ticket.slug}</Slug>
					<Dot $color={PRIORITY_COLORS[ticket.priority]} />
				</SlugGroup>
				<Meta>{createdStr}</Meta>
			</TopRow>
			<Subject>{ticket.subject}</Subject>
			<BottomRow>
				<PersonGroup>
					{ticket.requester_name && (
						<Avatar $bg="#6366f1" $fg="#ffffff">
							{getInitials(ticket.requester_name)}
						</Avatar>
					)}
					<RequesterName>{ticket.requester_name ?? 'Unknown'}</RequesterName>
				</PersonGroup>
				{ticket.assignee_name ? (
					<Avatar $bg="#eeedff" $fg="#3d36b3" $weight={600}>
						{getInitials(ticket.assignee_name)}
					</Avatar>
				) : (
					<Avatar $bg="#f1f5f9" $fg="#94a3b8" $weight={600}>
						?
					</Avatar>
				)}
			</BottomRow>
		</Card>
	);
}
